import { HeadersFunction, LoaderFunctionArgs, MetaArgs, redirect } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import { GetContentPageDocument, GetContentPagesForSitemapDocument, GetWebsiteRedirectsDocument } from '@theprintbar/graphql';
import { runSudoGraphql } from '@theprintbar/keystone/context';
import { invariantNotFound } from '@theprintbar/shared';
import { TitleSection } from '@theprintbar/ui';
import { Fragment } from 'react';
import { SitemapFunction } from 'remix-sitemap';
import { serverOnly$ } from 'vite-env-only/macros';
import { contentMeta } from '~/utils/contentMeta';
import { getComponent } from './utils/getComponent';
import { getContentStructuredData } from './utils/getContentStructuredData';
import { contentPageLoaderResponse } from './utils/getLoaderData';

export function meta({ data, matches }: MetaArgs<typeof loader>) {
	if (!data) return [];
	return [...contentMeta({ data: data, matches }), getContentStructuredData(data)];
}

export const headers: HeadersFunction = ({ loaderHeaders }) => ({
	'Cache-Control': loaderHeaders.get('Cache-Control') || '',
});

export const sitemap: SitemapFunction = serverOnly$(async ({}) => {
	const { contentPages } = await runSudoGraphql(GetContentPagesForSitemapDocument);

	return (
		contentPages?.map(({ slug, updatedAt }) => {
			return {
				loc: slug,
				lastmod: new Date(updatedAt).toISOString(),
				priority: 0.5,
			};
		}) || []
	);
})!;

export async function loader({ request, params }: LoaderFunctionArgs) {
	let slug = params['*'];
	invariantNotFound(slug, 'No slug found');

	const { contentPage } = await runSudoGraphql(GetContentPageDocument, { where: { slug } });

	if (!contentPage) {
		//Ensure the slug starts with a /
		if (!slug.startsWith('/')) {
			slug = '/' + slug;
		}

		const { websiteRedirects } = await runSudoGraphql(GetWebsiteRedirectsDocument, { where: { from: { equals: slug } } });

		if (websiteRedirects?.[0]?.to) {
			return redirect(`${websiteRedirects[0].to}${new URL(request.url).search}`, 301);
		}

		throw new Response('Not Found', { status: 404 });
	}

	return contentPageLoaderResponse(contentPage, request);
}

export default function () {
	const { contentPage } = useLoaderData<typeof loader>();

	return (
		<>
			<h1 className="hidden">{contentPage.headingTag || contentPage.metaTitle}</h1>
			{contentPage.pageComponents?.map((type, index) => {
				const previousType = contentPage.pageComponents?.[index - 1];
				const nextType = contentPage.pageComponents?.[index + 1];

				const { component, titleSectionProps, titleContainer } = getComponent({
					type,
					previousType,
					nextType,
					contentPage,
					index,
				});

				return (
					<Fragment key={type}>
						{titleContainer ? (
							<TitleSection content={titleContainer} {...titleSectionProps}>
								{component}
							</TitleSection>
						) : (
							component
						)}
					</Fragment>
				);
			})}
		</>
	);
}
